import type { EntityPatchRequest } from '../../types/gotypes'

/**
 * Hand corrections to one entity: naming a metric, reading a typed number, and
 * turning "what is on screen now" into the MERGE the PATCH endpoint wants.
 *
 * 🔴 WHY THIS MODULE EXISTS AT ALL, AND WHY IT IS THIS CAREFUL (#1194, #1196).
 * Sky's U35 data is gone from production — no `u35` under any spelling, on any
 * entity or pipeline. This screen and the CSV upload are the only two routes by
 * which it comes back. The way it was lost is worth stating, because every rule
 * below is a direct answer to one half of it:
 *
 *   1. THE NAME. Some spreadsheets said "U35%", some said "U35 Viewers", and
 *      only one spelling was ever recognised. So a typed name goes through the
 *      SAME normaliser the CSV headers go through — `normalizeHeaderToLabelKey`
 *      in `goapi/pkg/server/batch_import.go` — and `normalizeMetricKey` below
 *      mirrors it step for step.
 *
 *   2. THE PLACE. Numbers were filed into the labels bag, where they are
 *      strings, and every numeric read (sliders, metric ranges, cultural
 *      relevance) saw nothing. It failed silently for months. The architectural
 *      rule is "no numbers in labels"; the server enforces it with a 400 that
 *      names the key, and this module says the same thing at the field so the
 *      user learns it before the round trip rather than after.
 *
 * It is pure — no React, no fetching — for the same reason `entityQuery.ts` is:
 * the rules are worth testing without a render, and a rule that only exists
 * inside a component is a rule that gets copied the next time somebody needs it.
 */

/**
 * The browser's copy of `normalizeHeaderToLabelKey`
 * (`goapi/pkg/server/batch_import.go`), step for step and in the same order.
 *
 * ⚠ THE TWO MUST AGREE. A name typed here and the same name in a spreadsheet
 * column have to land on ONE key — that is the entire lesson of #1194, where
 * two spellings of U35 became two things nothing could reconcile. The Go
 * function's own documented examples are the test cases in entityEdits.test.ts,
 * copied verbatim, so a change to either side fails there.
 *
 * It normalises; it does not validate. "35 Under" comes out as `35_under`,
 * which is tidy and still illegal — see metricKeyError.
 */
export function normalizeMetricKey(input: string): string {
  let result = input

  // 1. Drop parenthesised notes: "Video Reach (SUM)" is one metric, not two.
  result = result.replace(/\s*\([^)]*\)/g, '')

  // 2. A pipe and everything after it — Sprinklr exports append provenance.
  const pipe = result.indexOf('|')
  if (pipe >= 0) result = result.slice(0, pipe)

  // 3. Lower case and trim.
  result = result.toLowerCase().trim()

  // 4. `%` becomes the WORD, before the character strip below removes it. This
  //    is what makes "U35%" and "U35 percent" the same key rather than "u35"
  //    and "u35_percent".
  result = result.replace(/%/g, '_percent')

  // 5. Spaces to underscores.
  result = result.replace(/ /g, '_')

  // 6. Anything else is dropped rather than transliterated.
  result = result.replace(/[^a-z0-9_]/g, '')

  // 7. Collapse runs and trim, so "Reach -- total" is one underscore not three.
  result = result.replace(/_+/g, '_').replace(/^_+|_+$/g, '')

  return result
}

/** The server's bounds, mirrored (`autocoder_entities.go`). */
const METRIC_KEY_MAX = 64
const LABEL_KEY_MAX = 128

/** The server's shape rule for a metric key, mirrored. */
const METRIC_KEY_PATTERN = /^[a-z][a-z0-9_]*$/

/**
 * Why the server would refuse this metric key, or null if it would not.
 *
 * 🔴 SAID HERE BECAUSE THE NORMALISER IS NOT A GUARANTEE. "35 Under"
 * normalises to `35_under` — lower_snake_case, obviously reasonable, and
 * rejected, because the pattern requires a letter first. Without this the user
 * types a sensible name, watches it turn into a sensible-looking key, and gets a
 * 400 from the save button naming a rule nothing on screen mentioned.
 *
 * The message names the fix rather than quoting the regex.
 */
export function metricKeyError(key: string): string | null {
  if (key === '') return 'Give the metric a name'
  if (key.length > METRIC_KEY_MAX) return `A metric name can be at most ${METRIC_KEY_MAX} characters`
  if (!METRIC_KEY_PATTERN.test(key)) {
    if (/^[0-9]/.test(key)) {
      return 'A metric name has to start with a letter — try “u35_under” rather than “35_under”'
    }
    return 'A metric name can only use lower-case letters, digits and underscores (e.g. u35_pct)'
  }
  return null
}

/**
 * Why the server would refuse this label key, or null if it would not.
 *
 * Deliberately LOOSER than the metric rule, matching the server: label keys are
 * free-form across the whole product — channel manifests, importers and ThemeAI
 * all mint them — so a pattern here would reject keys the rest of the system
 * happily writes. Only the two things that are always wrong are refused.
 */
export function labelKeyError(key: string): string | null {
  if (key === '') return 'Give the label a name'
  if (key.trim() !== key) {
    // A trailing space is invisible on screen and makes a different key.
    return 'A label name cannot start or end with a space'
  }
  if (key.length > LABEL_KEY_MAX) return `A label name can be at most ${LABEL_KEY_MAX} characters`
  return null
}

export type MetricInputResult =
  | { ok: true; value: number }
  | { ok: true; remove: true }
  | { ok: false; error: string }

/**
 * Read what a human typed into a metric field.
 *
 * Sky's numbers are pasted out of spreadsheets, so thousands separators and a
 * trailing unit are the normal case rather than the exotic one.
 *
 * 🔴 A PERCENT SIGN IS STRIPPED, NOT DIVIDED BY A HUNDRED. A `percent` metric is
 * stored as the percent NUMBER — `formatMetricValue` renders 36.8 as "36.8%"
 * (metrics-config.ts). Dividing would store 0.368, which then renders as "0.4%":
 * an edit that moves the value by two orders of magnitude and looks entirely
 * plausible on the way past.
 *
 * A BLANK FIELD IS A REMOVAL, never a zero. "We have no U35 figure for this
 * post" and "this post's U35 is 0" are different claims, and conflating them is
 * how a missing number becomes a wrong one.
 */
export function parseMetricInput(text: string): MetricInputResult {
  const trimmed = text.trim()
  if (trimmed === '') return { ok: true, remove: true }

  // Thousands separators (comma or space) and ONE trailing percent sign. Nothing
  // else is forgiven: "36.8%" is a number a human typed, "36.8% of under-35s" is
  // a sentence, and guessing which number is inside a sentence is how the wrong
  // one gets stored.
  const cleaned = trimmed.replace(/%$/, '').replace(/[,\s]/g, '')
  if (cleaned === '' || !/^[-+]?(\d+\.?\d*|\.\d+)$/.test(cleaned)) {
    return {
      ok: false,
      error: 'This has to be a number — text belongs in a label, not a metric',
    }
  }

  const value = Number(cleaned)
  if (!Number.isFinite(value)) {
    // Unreachable through the pattern above, but JSON cannot carry Infinity or
    // NaN at all, so a value that got here would fail at the wire rather than
    // at the field.
    return { ok: false, error: 'This has to be a number the system can store' }
  }
  return { ok: true, value }
}

/**
 * A metric name already in use that this new key probably meant, or null.
 *
 * 🔴 WHY A SUGGESTION IS NEEDED AT ALL, AND IT IS THE SHARPEST TRAP ON THIS
 * SCREEN. The CSV import path has TWO stages: normalise the header, then
 * resolve the result through the catalog's LEGACY ALIASES
 * (`goapi/pkg/autocoder/entitymetrics/catalog.go`), where `u35_percent` and
 * `u35_viewers` both land on the canonical `u35_pct`. The manual PATCH endpoint
 * has only the FIRST stage — it validates the key's shape and writes it
 * verbatim.
 *
 * So a user typing "U35%" here gets `u35_percent`, which is spelled correctly,
 * saves without error, and is NOT the key any chart asks for (`KPI_METRIC_KEYS`
 * names `u35_pct`). The number would be filed, invisible, and look filed — the
 * same shape of failure as #1194, arriving through the door built to prevent it.
 *
 * This cannot be fully fixed in the browser: EN-T20 deleted the frontend's own
 * metric catalog on purpose, so the alias map is not here to consult and a
 * hard-coded copy would be the second source of truth that rule exists to
 * forbid. What the browser CAN do is notice that the key is not one the job
 * already carries and say so, and point at a near neighbour that is.
 *
 * The match is deliberately blunt — same first underscore-token — because it
 * only ever runs for a key that is NOT already known, so the noisy near-misses
 * between two real metrics (`video_reach` / `video_views`) never arise.
 */
export function suggestKnownMetricKey(key: string, known: string[]): string | null {
  if (key === '' || known.includes(key)) return null
  const token = key.split('_')[0]
  if (token === '') return null
  const matches = known.filter((k) => k !== key && k.split('_')[0] === token)
  if (matches.length === 0) return null
  // Shortest first, then alphabetical: a canonical key is usually the terser of
  // the pair (`u35_pct` beside `u35_percent`), and ties must not depend on the
  // order the summary happened to arrive in.
  return [...matches].sort((a, b) => a.length - b.length || a.localeCompare(b))[0]
}

/**
 * Does this look like a bare number? Used to refuse it as a label BEFORE the
 * round trip, in the same direction and with the same reasoning as the server.
 *
 * ⚠ A label that merely CONTAINS digits is fine — "EB&A - Sky Q 2026" is a
 * name. What is refused is a value that IS a number, because that is a metric
 * filed in the wrong place, which is #1194 exactly.
 */
function looksNumeric(value: string): boolean {
  const result = parseMetricInput(value)
  return result.ok === true && !('remove' in result)
}

/** What the entity currently says, as loaded. */
export interface EntityEditOriginal {
  labels: Record<string, string>
  /** Metric key → its stored number (`EntityMetricReading.value`). */
  metrics: Record<string, number>
}

/** What is on screen. Both bags hold the RAW text of the inputs. */
export interface EntityEditDraft {
  /** Label key → value as typed. Blank deletes an existing key. */
  labels: Record<string, string>
  /** Metric key → value as typed. Blank deletes an existing key. */
  metrics: Record<string, string>
}

export interface EntityPatchResult {
  /**
   * The body to send, or null when there is nothing to send — either nothing
   * changed, or something is in error.
   */
  patch: EntityPatchRequest | null
  /** Field id (`label:<key>` / `metric:<key>`) → the message to show under it. */
  errors: Record<string, string>
}

/**
 * Turn "what is on screen" into the PATCH body.
 *
 * 🔴 IT IS A MERGE, AND ONLY CHANGED KEYS TRAVEL. A key absent from the body is
 * left untouched by the server, which is what lets two people correct different
 * fields of one post without either discarding the other's work. Sending a key
 * that did not change is not merely wasteful: it restamps that metric row's
 * provenance as `manual` and overwrites whatever landed between this page
 * loading and this save.
 *
 * 🔴 A CLEARED FIELD GOES ON THE WIRE AS null. The server refuses an
 * empty-string label with a 400 that says "send null to delete it", so passing
 * "" through would turn the ordinary act of clearing a field into an error the
 * user cannot act on. Clearing a row that did not exist yet is nothing at all —
 * an empty row somebody added and thought better of.
 *
 * 🔴 ONE BAD FIELD BLOCKS THE WHOLE SAVE. The endpoint validates before it
 * writes, so a body with one bad key writes nothing; sending the good half
 * anyway would leave the user on a page that saved some of what they typed with
 * no way to tell which half.
 */
export function buildEntityPatch(
  original: EntityEditOriginal,
  draft: EntityEditDraft,
): EntityPatchResult {
  const errors: Record<string, string> = {}
  const labels: Record<string, string | null> = {}
  const metrics: Record<string, number | null> = {}

  for (const [key, raw] of Object.entries(draft.labels)) {
    const keyError = labelKeyError(key)
    if (keyError) {
      errors[`label:${key}`] = keyError
      continue
    }
    const value = raw.trim()
    const existing = Object.prototype.hasOwnProperty.call(original.labels, key)

    if (value === '') {
      // Blank on an existing key deletes it; blank on a key that was never
      // there is an abandoned new row.
      if (existing) labels[key] = null
      continue
    }
    if (looksNumeric(value)) {
      errors[`label:${key}`] =
        'That is a number, so it belongs in a metric — a number stored as a label is invisible to every chart'
      continue
    }
    if (!existing || original.labels[key] !== value) labels[key] = value
  }

  for (const [key, raw] of Object.entries(draft.metrics)) {
    const keyError = metricKeyError(key)
    if (keyError) {
      errors[`metric:${key}`] = keyError
      continue
    }
    const parsed = parseMetricInput(raw)
    const existing = Object.prototype.hasOwnProperty.call(original.metrics, key)

    if (!parsed.ok) {
      errors[`metric:${key}`] = parsed.error
      continue
    }
    if ('remove' in parsed) {
      if (existing) metrics[key] = null
      continue
    }
    if (!existing || original.metrics[key] !== parsed.value) metrics[key] = parsed.value
  }

  if (Object.keys(errors).length > 0) return { patch: null, errors }

  const patch: EntityPatchRequest = {}
  if (Object.keys(labels).length > 0) patch.labels = labels
  if (Object.keys(metrics).length > 0) patch.metrics = metrics
  if (Object.keys(patch).length === 0) return { patch: null, errors }
  return { patch, errors }
}
